import { DictionaryRecordContextProvider } from "@/components/dictionaries/DictionaryContext";
import DictionaryLoader from "@/components/dictionaries/DictionaryLoader";
import DictionaryPageHeader from "@/components/dictionaries/DictionaryPageHeader";
import { useToast } from "@/components/ui/use-toast";
import { Module } from "@/constants";
import { DictionaryContext } from "@/containers/dictionaries/types";
import { useQueryCacheUpdater } from "@/hooks/useQueryCacheUpdater";
import { useFormUnsavedChangesWarning } from "@/hooks/useFormUnsavedChangesWarning";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";
import { ApiError } from "@/services/api";
import { zodResolver } from "@hookform/resolvers/zod";
import { MutationFunction, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { DefaultValues, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDepartmentBasedProjectRatesDictionary } from "@/modules/departmentBasedProjectRates/hooks/useDepartmentBasedProjectRatesDictionary";
import {
  ReadDepartmentBasedProjectRates,
  WriteDepartmentBasedProjectRates,
} from "@/schemas/departmentBasedProjectRates";
import { DepartmentBasedProjectRatesForm } from "@/modules/departmentBasedProjectRates/components/DepartmentBasedProjectRatesForm";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";

export default function DepartmentBasedProjectRatesEditor({
  id,
}: DictionaryContext) {
  const { t } = useTranslation();
  const config = useDepartmentBasedProjectRatesDictionary();
  const navigate = useNavigate();
  const { toast } = useToast();
  const labels = config.getLabels(t);
  const { updateQueryDataForExistingItem } = useQueryCacheUpdater(
    Module.DepartmentBasedProjectRates,
  );
  const { can_update } = useModulePermissions();
  const query = useQuery<ReadDepartmentBasedProjectRates>({
    queryKey: [config.ns, config.id, id],
    queryFn: async () => {
      const result = await config.service.get(id);
      return result as ReadDepartmentBasedProjectRates;
    },
  });

  const mutationFn: MutationFunction<
    ReadDepartmentBasedProjectRates,
    WriteDepartmentBasedProjectRates
  > = async (values) => {
    const response = (await config.service.patch(
      id,
      values,
    )) as ReadDepartmentBasedProjectRates;

    return {
      ...values,
      id: response?.id,
    } as unknown as ReadDepartmentBasedProjectRates;
  };

  const mutation = useMutationWithErrorHandling<
    ReadDepartmentBasedProjectRates,
    ApiError,
    WriteDepartmentBasedProjectRates,
    unknown
  >({
    mutationFn,
    errorTitle: labels.errors.update,
    onSuccess: async (response) => {
      const id = response.id;

      navigate(`${config.path}/${id}`);
      toast({
        title: labels.success.update,
      });
      updateQueryDataForExistingItem(response);
    },
  });

  const form = useForm<WriteDepartmentBasedProjectRates>({
    resolver: zodResolver(config.schema),
    defaultValues: (config.defaultValues ||
      {}) as DefaultValues<WriteDepartmentBasedProjectRates>,
    disabled: mutation.isPending || !can_update,
  });

  useFormUnsavedChangesWarning(form);

  useEffect(() => {
    if (query.data) {
      const formValues = Object.fromEntries(
        Object.entries(query.data).filter(([, value]) => value != null),
      ) as DefaultValues<WriteDepartmentBasedProjectRates>;
      form.reset(formValues);
    }
  }, [form, query.data]);

  return (
    <DictionaryRecordContextProvider
      value={{
        query,
        config,
        mutation,
        form,
        id,
      }}
    >
      <DictionaryPageHeader />
      {query.isLoading && <DictionaryLoader />}
      {!query.isLoading && <DepartmentBasedProjectRatesForm />}
    </DictionaryRecordContextProvider>
  );
}
